import { ProductPageProvider } from "@/components";
import { Alert, Button, Form, Input, Spin } from "antd";
import type { AxiosRequestConfig } from "axios";
import axios from "axios";
import { Building2Icon, LockIcon, LogInIcon, MailIcon, ShieldCheckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { PortalBrand } from "../components/PortalProduct";
import { portalRequest, refreshPortalSession, setPortalSession } from "../lib/auth";
import { resolvePortalBrandImage } from "../lib/branding";
import { getPortalLandingPath } from "../lib/routes";
import { useTenant } from "../tenant/TenantContext";

type PortalAxiosRequestConfig = AxiosRequestConfig & { _skipAuthRefresh?: boolean };
type PortalSessionResponse = {
  actorType: "tenant_user" | "agency_user";
};

const getPortalLoginError = (error: unknown) => {
  if (!axios.isAxiosError<{ error?: string }>(error)) return "Unable to sign in";
  return error.response?.data?.error || "Unable to sign in";
};

/** Public landing and sign-in page for a tenant's agency portal. */
const AgentPortalHomePage = () => {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;

    refreshPortalSession()
      .then(() => portalRequest<PortalSessionResponse>({ url: "/api/agent-portal/session", method: "get" }))
      .then((response) => {
        if (!cancelled) navigate(getPortalLandingPath(response.data.actorType), { replace: true });
      })
      .catch(() => {
        if (!cancelled) setCheckingSession(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (checkingSession) {
    return (
      <ProductPageProvider>
        <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB]">
          <Spin size="large" />
        </main>
      </ProductPageProvider>
    );
  }

  const backgroundImage = resolvePortalBrandImage(tenant.branding?.loginBackgroundImage);
  const signInContent = (
    <div className="w-full">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-(--brand-primary)">Partner portal</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#131B2A]">Welcome back</h2>
      <p className="mt-2 text-sm leading-6 text-gray-500">Sign in with the account provided by your organisation.</p>

      <Form
        className="mt-8"
        layout="vertical"
        requiredMark="optional"
        onFinish={async (values) => {
          try {
            setSubmitting(true);
            setError(null);
            const response = await axios.post("/api/agent-portal/auth/login", { ...values, actorType: "agency_user" }, {
              withCredentials: true,
              _skipAuthRefresh: true,
            } as PortalAxiosRequestConfig);
            setPortalSession(response.data.token, response.data.actor);
            navigate(getPortalLandingPath(response.data.actor.type));
          } catch (requestError: unknown) {
            setError(getPortalLoginError(requestError));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {error ? <Alert className="mb-5" type="error" showIcon message={error} /> : null}
        <Form.Item
          label="Email address"
          name="email"
          rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
        >
          <Input
            prefix={<MailIcon size={15} className="text-gray-400" />}
            autoComplete="email"
            placeholder="name@company.com"
          />
        </Form.Item>
        <Form.Item label="Password" name="password" rules={[{ required: true, message: "Enter your password" }]}>
          <Input.Password
            prefix={<LockIcon size={15} className="text-gray-400" />}
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </Form.Item>
        <div className="-mt-2 mb-5 text-right">
          <Link to="/forgot-password" className="text-xs font-medium text-(--brand-primary) hover:text-[#131B2A]">
            Forgot password?
          </Link>
        </div>
        <Button type="primary" htmlType="submit" block icon={<LogInIcon size={16} />} loading={submitting}>
          Sign in
        </Button>
      </Form>
      <p className="mt-5 border-t border-gray-200 pt-5 text-xs leading-5 text-gray-500">
        {tenant.selfSignupEnabled ? (
          <>
            New agency?{" "}
            <Link to="/signup" className="font-medium text-(--brand-primary) hover:text-[#131B2A]">
              Request an account
            </Link>
            . {tenant.tenantName || "The organisation"} reviews every request.
          </>
        ) : (
          "New agency user? Ask your organisation contact to create your portal account."
        )}
      </p>
    </div>
  );

  if (backgroundImage) {
    return (
      <ProductPageProvider>
        <main className="grid min-h-[100dvh] bg-white text-[#131B2A] lg:grid-cols-[minmax(0,2fr)_minmax(400px,1fr)]">
          <section
            className="relative hidden min-h-[100dvh] overflow-hidden bg-(--brand-secondary) bg-cover bg-center lg:flex lg:items-end"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-black/35" />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 to-transparent" />
            <div className="relative z-10 max-w-3xl px-10 pb-12 xl:px-16 xl:pb-16">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/75">
                <ShieldCheckIcon size={15} strokeWidth={1.8} /> Secure agency workspace
              </p>
              <h1 className="portal-title mt-5 text-4xl font-semibold leading-[1.08] text-white xl:text-5xl">
                Plan and confirm travel quotes with confidence.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/75 xl:text-base">
                Access {tenant.tenantName}&apos;s curated inventory and follow every itinerary from one shared
                workspace.
              </p>
            </div>
          </section>

          <aside className="flex min-h-[100dvh] flex-col bg-white px-6 sm:px-10 lg:border-l lg:border-black/5 lg:px-12 xl:px-16">
            <header className="flex min-h-24 items-center border-b border-gray-100 py-5">
              <PortalBrand name={tenant.tenantName || "Partner portal"} image={tenant.branding?.logo} compact navbar />
            </header>
            <div className="flex flex-1 items-center py-10">
              <div className="mx-auto w-full max-w-sm">{signInContent}</div>
            </div>
            <footer className="border-t border-gray-100 py-5 text-xs text-gray-400">
              Secure access for authorised agency partners
            </footer>
          </aside>
        </main>
      </ProductPageProvider>
    );
  }

  return (
    <ProductPageProvider>
      <main className="min-h-[100dvh] bg-(--portal-canvas) text-[#131B2A]">
        <div className="grid min-h-[100dvh] lg:grid-cols-[minmax(0,1fr)_480px]">
          <section className="flex flex-col bg-(--brand-secondary) px-5 py-8 text-white sm:px-8 lg:px-14 lg:py-12">
            <span className="inline-flex w-fit rounded-lg bg-white px-2 py-0.5">
              <PortalBrand name={tenant.tenantName || "Partner portal"} image={tenant.branding?.logo} compact navbar />
            </span>
            <div className="my-auto max-w-2xl py-10">
              <span className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-(--portal-accent)">
                <ShieldCheckIcon size={14} strokeWidth={1.8} /> Secure agency workspace
              </span>
              <h1 className="portal-title mt-6 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
                Plan and confirm travel quotes with confidence.
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-7 text-white/70 sm:text-base">
                Access {tenant.tenantName}&apos;s curated inventory, prepare complete quotes, and follow every trip from
                one shared workspace.
              </p>
              <div className="mt-8 grid max-w-xl gap-3 sm:grid-cols-3">
                {["Curated inventory", "Live request pricing", "Itinerary tracking"].map((item) => (
                  <div key={item} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <Building2Icon size={16} className="text-(--portal-accent)" strokeWidth={1.8} />
                    <p className="mt-3 text-xs font-semibold text-white">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <aside className="flex items-center bg-(--portal-canvas) px-5 py-10 sm:px-8 lg:px-12">
            {signInContent}
          </aside>
        </div>
      </main>
    </ProductPageProvider>
  );
};

export default AgentPortalHomePage;
